import { useEffect, useState } from 'preact/hooks';
import { type Flag, type FlagState, loadFlags, setFlagState, setMeChosen } from '../lib/admin';
import { projectLabel } from '../lib/config';
import { config } from '../lib/supabase';
import { goBack } from '../lib/router';
import { useNotice } from '../shell/notice';
import { Segmented } from '../ui/Segmented';
import { BackLink } from '../ui/BackLink';

// Feature flags (backlog n045): an owner-only page listing each flag, its state (off · on for
// chosen people · on for everyone) and whether you're one of the chosen people, with Undo.
// Each project (test, live) has its own flags; the page says which one it's changing.

const STATES: { key: FlagState; name: string }[] = [
  { key: 'off', name: 'Off' },
  { key: 'chosen', name: 'Chosen people' },
  { key: 'everyone', name: 'Everyone' },
];
const stateName = (s: FlagState) => STATES.find((x) => x.key === s)!.name.toLowerCase();

export function FlagsPage({ userId, isAdmin }: { userId: string; isAdmin: boolean }) {
  const notice = useNotice();
  const [flags, setFlags] = useState<Flag[] | null>(null);
  const [problem, setProblem] = useState('');
  const where = projectLabel(config);

  const reload = () =>
    loadFlags(userId)
      .then((f) => {
        setFlags(f);
        setProblem('');
      })
      .catch(() => setProblem('The flags couldn’t be read. Check your connection and try again.'));

  useEffect(() => {
    if (isAdmin) reload();
  }, [isAdmin]);

  if (!isAdmin) {
    return (
      <div class="page">
        <h1 class="page-title">Feature flags</h1>
        <p class="heads-up">Only the admin can see this page.</p>
      </div>
    );
  }

  async function change(f: Flag, run: () => Promise<void>, undo: () => Promise<void>, message: string) {
    try {
      await run();
      await reload();
      notice({
        message,
        undo: () => {
          undo()
            .then(reload)
            .then(() => notice({ message: `${f.key}: put back` }))
            .catch(() => setProblem('That couldn’t be undone. Reload the page and check the flag.'));
        },
      });
    } catch {
      setProblem('That change wasn’t saved. Check your connection and try again.');
    }
  }

  return (
    <div class="page flags-page">
      <BackLink where="Shelves" onClick={() => goBack('/shelves')} />
      <h1 class="page-title">Feature flags</h1>
      <p class="heads-up">
        Changing the <b>{where}</b> project. Each section is built behind its flag and switched on for you first;
        a flag can switch a broken section off without a new release.
      </p>
      {problem ? (
        <p class="auth-problem" role="alert">
          {problem}
        </p>
      ) : null}
      {flags === null ? (
        <p>Reading the flags…</p>
      ) : (
        <ul class="flags">
          {flags.map((f) => (
            <li key={f.key} class="flag" aria-label={f.key}>
              <div class="flag-name">
                <b>{f.key}</b>
                <span class="flag-desc">{f.description}</span>
              </div>
              <Segmented
                label={`${f.key} state`}
                options={STATES}
                value={f.state}
                onChange={(s) =>
                  s !== f.state &&
                  change(
                    f,
                    () => setFlagState(f.key, s),
                    () => setFlagState(f.key, f.state),
                    `${f.key}: ${stateName(s)}`,
                  )
                }
              />
              {f.state === 'chosen' ? (
                <label class="flag-me">
                  <input
                    type="checkbox"
                    checked={f.me}
                    onChange={() =>
                      change(
                        f,
                        () => setMeChosen(f.key, userId, !f.me),
                        () => setMeChosen(f.key, userId, f.me),
                        f.me ? `${f.key}: you're no longer chosen` : `${f.key}: on for you`,
                      )
                    }
                  />
                  On for me
                  {f.others ? <span class="flag-desc"> · and {f.others} other {f.others === 1 ? 'person' : 'people'}</span> : null}
                </label>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
